/**
 * Searchable member picker (GET api/members/search). Used to merge records and to add people to
 * a household. Needs Admin or Member.Read.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMemberService, type Member } from '@actschurch/ccms-sdk';
import { Select } from '@/components/ui';
import { memberFullName } from './memberFormat';

interface Props {
  label?: string;
  description?: string;
  placeholder?: string;
  value: Member | null;
  onChange: (member: Member | null) => void;
  /** Member ids that may not be picked (for example the profile being edited). */
  excludeIds?: number[];
  required?: boolean;
  disabled?: boolean;
}

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function memberOptionLabel(m: Member): string {
  const name = memberFullName(m) || `Member ${m.memberID}`;
  const extra = [m.memberCode, m.email || m.cellNumber].filter(Boolean).join(' · ');
  return extra ? `${name} (${extra})` : name;
}

export function MemberSearchSelect({ label = 'Member', description, placeholder = 'Type a name, email or phone number', value, onChange, excludeIds = [], required, disabled }: Props) {
  const memberService = useMemberService();
  const [search, setSearch] = useState('');
  const term = useDebounced(search.trim());

  const results = useQuery({
    queryKey: ['members', 'search', term],
    queryFn: () => memberService.searchMembers(term, 20),
    enabled: term.length >= 2,
    staleTime: 30_000,
  });

  const options = useMemo(() => {
    const rows = (results.data ?? []).filter((m) => !excludeIds.includes(m.memberID) && !m.isDeleted);
    if (value && !rows.some((m) => m.memberID === value.memberID)) rows.unshift(value);
    return rows.map((m) => ({ value: String(m.memberID), label: memberOptionLabel(m), member: m }));
  }, [results.data, excludeIds, value]);

  return (
    <Select
      label={label}
      description={description}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      searchable
      clearable
      data={options.map(({ value: v, label: l }) => ({ value: v, label: l }))}
      value={value ? String(value.memberID) : null}
      searchValue={search}
      onSearchChange={setSearch}
      filter={({ options: opts }) => opts}
      nothingFoundMessage={term.length < 2 ? 'Type at least two letters' : results.isFetching ? 'Searching' : 'No members found'}
      onChange={(v) => onChange(v ? options.find((o) => o.value === v)?.member ?? null : null)}
    />
  );
}
